import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Car, Flag, MapPin } from "lucide-react";

// Icônes Leaflet personnalisées (départ / arrivée)
function badge(svgInner, color = "#1E6FB8", size = 32) {
  const html = `
    <div style="
      width:${size}px;height:${size}px;border-radius:14px;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 4px 12px rgba(15,46,81,0.32);
      border:2px solid #fff;
    ">
      <svg width="${size * 0.55}" height="${size * 0.55}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${svgInner}</svg>
    </div>`;
  return L.divIcon({
    className: "carpool-marker",
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const ICON_FROM = badge('<circle cx="12" cy="11" r="3"/><path d="M12 22s-7-7-7-12a7 7 0 0114 0c0 5-7 12-7 12z"/>', "#3B82C4", 30);
const ICON_TO = badge('<rect x="4" y="3" width="3" height="18"/><path d="M7 4h13l-3 5 3 5H7"/>', "#E5856A", 30);

function FitBounds({ from, to }) {
  const map = useMap();
  if (from && to) {
    const bounds = L.latLngBounds([from, to]).pad(0.3);
    map.fitBounds(bounds, { animate: false, maxZoom: 14 });
  }
  return null;
}

/**
 * Mini carte de trajet co-voiturage.
 * Affiche un marqueur départ + arrivée + ligne directe entre les deux.
 * Si une seule coordonnée est connue, centre sur Istres.
 */
export default function CarpoolTripMap({
  fromLat,
  fromLng,
  toLat,
  toLng,
  fromLabel,
  toLabel,
  height = 280,
}) {
  const from = useMemo(
    () => (typeof fromLat === "number" && typeof fromLng === "number" ? [fromLat, fromLng] : null),
    [fromLat, fromLng]
  );
  const to = useMemo(
    () => (typeof toLat === "number" && typeof toLng === "number" ? [toLat, toLng] : null),
    [toLat, toLng]
  );

  const center = from || to || [43.5132, 4.9897]; // Centre Istres
  const zoom = from && to ? 13 : 13;

  if (!from && !to) {
    return (
      <div
        className="w-full rounded-2xl bg-cream-50 border border-cream-200 grid place-items-center text-plum-700/40"
        style={{ height }}
        data-testid="carpool-map-empty"
      >
        <div className="text-center px-4">
          <MapPin size={26} className="mx-auto mb-1.5" />
          <p className="text-[12px] font-semibold">Carte indisponible</p>
          <p className="text-[11px]">Adresse non géocodée</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl overflow-hidden border border-cream-200 shadow-sm relative"
      style={{ height }}
      data-testid="carpool-map"
    >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {from && (
          <Marker position={from} icon={ICON_FROM}>
            <Popup>
              <div className="text-[13px]">
                <strong>Départ</strong>
                <br />
                {fromLabel || "Lieu de départ"}
              </div>
            </Popup>
          </Marker>
        )}
        {to && (
          <Marker position={to} icon={ICON_TO}>
            <Popup>
              <div className="text-[13px]">
                <strong>Arrivée</strong>
                <br />
                {toLabel || "Lieu d'arrivée"}
              </div>
            </Popup>
          </Marker>
        )}
        {from && to && (
          <Polyline positions={[from, to]} pathOptions={{ color: "#5B3A6B", weight: 4, dashArray: "6 6", opacity: 0.7 }} />
        )}
        {from && to && <FitBounds from={from} to={to} />}
      </MapContainer>
    </div>
  );
}

CarpoolTripMap.Legend = function Legend() {
  return (
    <div className="flex items-center gap-4 text-[11px] text-plum-700/70 mt-2">
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-full bg-[#3B82C4]" /> Départ
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm bg-[#E5856A]" /> Arrivée
      </span>
      <span className="inline-flex items-center gap-1.5 text-plum-700/55">
        <Car size={11} /> Trajet estimé (ligne droite)
      </span>
    </div>
  );
};
